import "@vscode/codicons/dist/codicon.css";
import "./workbench.css";

import {
    findReferences,
    formatDocument,
    jumpToDeclaration,
    jumpToDefinition,
    renameSymbol,
} from "@codemirror/lsp-client";
import type { EditorView } from "@codemirror/view";

import { GUEST_PROJECT, type IdfTargets } from "idfweb-build";
import { Registry } from "idfweb-components";
import type { ProjectReply, ProjectRequest, RuntimeToWorkbench } from "idfweb-runtime-protocol";
import { defineSetting, settings as settingsRegistry } from "idfweb-settings";
import type { MessageLevel, Reporter } from "idfweb-tasks";

import { cmakeCompletion } from "./cmake-completion.ts";
import { manifestCompletion } from "./manifest-completion.ts";
import { CommandRegistry, IS_MAC, keyLabel, type Command } from "./commands.ts";
import { editorKeys } from "./editor-setup.ts";
import { EditorArea, isMarkdown, viewKey, type OpenEditors, type PickLines, type ViewTab } from "./editors.ts";
import { copyDropped, type Dropped } from "./drop.ts";
import { directoryNodes, Explorer, plainNode, type Section, type TreeNode } from "./explorer.ts";
import { FileIndex } from "./files.ts";
import { LocationHistory } from "./history.ts";
import { findKeys, openReplace } from "./find-widget.ts";
import { fileIcon } from "./icons.ts";
import { ClangdLink, pathOf } from "./lsp.ts";
import { showMenu } from "./menu.ts";
import { Panel } from "./panel.ts";
import { fuzzy, QuickInput, type QuickItem } from "./quick-input.ts";
import { RuntimeLink } from "./runtime.ts";
import { SearchView } from "./search.ts";
import { isManifest, manifestView } from "./manifest-view.ts";
import { isSdkconfig, revealSetting, sdkconfigView } from "./sdkconfig-view.ts";
import { settingsTab } from "./settings-tab.ts";
import { shortcutsTab } from "./shortcuts.ts";
import type { FsDirEntry } from "idfweb-runtime-protocol";
import { Filetype } from "idfweb-vfs";

export interface WorkbenchOptions {
    /** The runtime worker's port for the workbench. */
    port: MessagePort;
    reporter: Reporter;
    /** Ask a question with a button for each action; undefined if it was dismissed. */
    ask: (level: MessageLevel, text: string, actions: readonly string[]) => Promise<string | undefined>;
    /** Flash the last build to the board. */
    flash: () => Promise<void>;
    /** Where the output panel goes: the page docks it beside the editor. */
    panelHost: HTMLElement;
    /** Called when a view of the panel is shown other than by a click on its tab; the page reveals the panel. */
    panelShown?: () => void;
    /** Called when the left side bar is shown or hidden, or its drawer opened or closed. */
    sidebarChanged?: (visible: boolean) => void;
    /** Called with the left side bar's width in pixels when a drag of its edge ends. */
    sidebarResized?: (width: number) => void;
    /** The tabs to open with a project in place of its README, as `editorsChanged` last reported them for it. */
    savedEditors?: () => OpenEditors | undefined;
    /** Called when the tabs change: opened, closed, shown, pinned or switched between text and rendering. */
    editorsChanged?: (editors: OpenEditors) => void;
    /** Run an `idfweb:` link in rendered Markdown, with the link's title and the document's folder. */
    action?: (href: string, title: string | undefined, dir: string) => void;
    /** Called with what the person did in the workbench, for the page's guide conditions. */
    event?: (event: WorkbenchEvent) => void;
    /**
     * Called when the opened tree `top` holds several projects and none at its
     * top; resolves to true if the page selected a project root, skipping user selection.
     */
    chooseProject?: (top: string, projects: string[]) => Promise<boolean>;
}

/**
 * What happened in the workbench: a command started to run, the quick input
 * opened with a prefix (">" is the palette), an editor saved a file, or a
 * build the workbench started ended.
 */
export type WorkbenchEvent =
    | { kind: "command"; id: string }
    | { kind: "quick"; prefix: string }
    | { kind: "saved"; path: string }
    | { kind: "built"; ok: boolean; elf?: string; message?: string };

export { keyLabel, type Command, type OpenEditors, type PickLines, type ViewTab };
export { resolve as resolvePath } from "./editors.ts";
export { showMenu, type MenuItem } from "./menu.ts";
export type { Section, TreeNode } from "./explorer.ts";

export interface Workbench {
    execute(id: string): Promise<unknown>;
    command(id: string): Command | undefined;
    /** The commands the palette lists now. */
    commands(): Command[];
    /** Add commands of the page's own to the palette and the keyboard. */
    register(...commands: Command[]): void;
    /** The quick input's prefix (">", "", "#"…) while it is open, or undefined. */
    quickPrefix(): string | undefined;
    hideQuick(): void;
    /** The output panel, for the page's own views such as the serial monitor. */
    panel: Pick<Panel, "addView" | "show" | "select" | "onSelect">;
    /** The chips the bundled ESP-IDF supports, for completion in `idf_component.yml`. */
    setTargets(targets: IdfTargets): void;
    /** Open a tab of the page's own in the editor area, or show it if it is open. */
    openView(tab: ViewTab): void;
    /** The tabs, as `editorsChanged` reports them. */
    openEditors(): OpenEditors;
    /** Open a file at the lines `pick` chooses, selected and marked, with `note` under them; resolves to why not, if so. */
    showLines(path: string, pick: PickLines, note: string): Promise<string | undefined>;
    /**
     * Open the sdkconfig `path` in its editor and show the row of the symbol
     * `name`; resolves to the row, or to why it cannot be shown.
     */
    showSetting(path: string, name: string): Promise<Element | string>;
    /** Open a file in a tab of its own and show it, Markdown rendered; resolves to false if there is no such file. */
    open(path: string): Promise<boolean>;
    /** Whether Go Back and Go Forward have a location to go to. */
    navigation(): { back: boolean; forward: boolean };
    /** Call `listener` when `navigation()` may have changed. */
    onNavigation(listener: () => void): void;
    /** Whether the left side bar is shown, or its drawer open. */
    sidebarVisible(): boolean;
    setSidebar(visible: boolean): void;
    /** Set the left side bar's width, as `sidebarResized` reported it. */
    setSidebarWidth(width: number): void;
    /** The page's own explorer sections, shown below the project and ESP-IDF. */
    setPageSections(sections: Section[]): void;
    /** Read the explorer's expanded rows again and redraw it. */
    refreshExplorer(): void;
}

const IDF = "/idf";
const OUTPUT = "Build";
const OVERWRITE = "Overwrite";
const REVERT = "Revert";

/** Explorer and editor settings; see idfweb-settings. */
const settings = {
    showIdf: defineSetting({
        key: "idfweb.explorer.showIdf",
        type: "boolean",
        title: "Show ESP-IDF",
        description:
            "Show the bundled ESP-IDF tree in the explorer next to the project, and search it with Find in Files.",
        group: ["Explorer"],
        default: false,
    }),
    groupGenerated: defineSetting({
        key: "idfweb.explorer.groupGenerated",
        type: "boolean",
        title: "Group Build and Managed Components",
        description:
            "List the build directory and managed_components at the end of the project, instead of among the sources.",
        group: ["Explorer"],
        default: true,
    }),
    wordWrap: defineSetting({
        key: "idfweb.editor.wordWrap",
        type: "boolean",
        title: "Word Wrap",
        description: "Wrap long lines at the width of the editor.",
        group: ["Editor"],
        default: true,
    }),
    tabSize: defineSetting({
        key: "idfweb.editor.tabSize",
        type: "number",
        title: "Tab Size",
        description: "The number of columns between tab stops, and of spaces in one indent level.",
        group: ["Editor"],
        default: 4,
        min: 1,
        max: 16,
        integer: true,
    }),
    insertSpaces: defineSetting({
        key: "idfweb.editor.insertSpaces",
        type: "boolean",
        title: "Insert Spaces",
        description:
            "Indent with spaces instead of tabs. A file that already indents with tabs or with spaces keeps its style.",
        group: ["Editor"],
        default: true,
    }),
};

/**
 * The page's own workbench: an explorer and a search view in a side bar, a
 * tabbed CodeMirror editor, a quick input for files, commands, lines and
 * symbols, and an output panel. It is a client of the runtime worker over the
 * port in `options`.
 */
export function createWorkbench(host: HTMLElement, options: WorkbenchOptions): Workbench {
    const runtime = new RuntimeLink(options.port);
    const registry = new CommandRegistry();
    const quick = new QuickInput();
    registry.onRun((id) => options.event?.({ kind: "command", id }));
    quick.onShow((prefix) => options.event?.({ kind: "quick", prefix }));
    const editors = new EditorArea(runtime, {
        saveFailed: (path, e) =>
            options.reporter.message("error", `Could not save ${display(path).relative}: ${briefly(e)}`),
        saved: (path) => options.event?.({ kind: "saved", path }),
        saveConflict: (path) =>
            options
                .ask(
                    "warn",
                    `${display(path).relative} changed on disk while it had unsaved edits here. ` +
                        "Overwrite it with the edits, or revert to its contents on disk?",
                    [OVERWRITE, REVERT],
                )
                .then((answer) => (answer === OVERWRITE ? "overwrite" : answer === REVERT ? "revert" : undefined)),
        followLink: (path, fragment) => void followLink(path, fragment),
        action: (href, title, dir) => options.action?.(href, title, dir),
        keys: (command) => registry.get(command)?.keys,
    });
    // Go Back and Go Forward, over the locations the editor reports.
    const history = new LocationHistory(
        (location) => editors.restore(location),
        () => editors.location,
    );
    editors.onLocation((location, left) => (left ? history.update(location) : history.record(location)));
    editors.wordWrap = settings.wordWrap.get();
    settings.wordWrap.onChange((on) => (editors.wordWrap = on));
    editors.tabSize = settings.tabSize.get();
    settings.tabSize.onChange((size) => (editors.tabSize = size));
    editors.insertSpaces = settings.insertSpaces.get();
    settings.insertSpaces.onChange((on) => (editors.insertSpaces = on));
    const index = new FileIndex(runtime);
    const panel = new Panel(
        (path, line, column) => void editors.open(path, { line, column }),
        () => options.panelShown?.(),
    );
    options.panelHost.classList.add("wb-scope");
    options.panelHost.append(panel.element);

    /** The opened tree, its name, the project root builds use, and where they go. */
    const project = { tree: "", name: "", root: GUEST_PROJECT, buildDir: `${GUEST_PROJECT}/build` };
    /** The root's entry that holds the build directory: `build` also holds every preset's. */
    const buildTop = () => {
        const { root, buildDir } = project;
        return buildDir.startsWith(`${root}/`) ? `${root}/${buildDir.slice(root.length + 1).split("/")[0]}` : buildDir;
    };
    const display = (path: string) => {
        const base = path.startsWith(project.tree + "/")
            ? project.tree
            : path.startsWith(IDF + "/")
              ? ""
              : GUEST_PROJECT;
        const relative = path.slice(base.length + 1);
        const slash = relative.lastIndexOf("/");
        return { name: relative.slice(slash + 1), folder: slash < 0 ? "" : relative.slice(0, slash), relative };
    };
    editors.relativeTo = (path) => {
        if (path.startsWith(project.tree + "/")) return [project.name, ...display(path).relative.split("/")];
        return path.split("/").filter(Boolean);
    };

    const clangd = new ClangdLink(runtime, {
        display: (path) => editors.open(path, { preview: true }),
        read: (path) => editors.text(path),
        restarted: () => editors.reconfigureLanguageServer(),
        exited: () => editors.reconfigureLanguageServer(false),
        log: (text) => panel.append("clangd", text),
    });
    editors.clangd = clangd;
    editors.cmakeCompletion = cmakeCompletion(
        runtime,
        (path) => editors.text(path),
        () => project.root,
    );
    let idfTargets: IdfTargets | undefined;
    editors.manifestCompletion = manifestCompletion(() => idfTargets);
    editors.fileViews.push(
        sdkconfigView(runtime),
        manifestView({
            runtime,
            registry: new Registry(),
            text: (path) => editors.text(path),
            replace: (path, edits) => editors.replace(path, edits),
            save: (path) => editors.save(path),
            isReadOnly: (path) => editors.isReadOnly(path),
            projectRoot: () => project.root,
            buildDir: () => project.buildDir,
            reconfigure: () => configure(true),
            targets: () => idfTargets,
        }),
    );

    // ---- layout ----------------------------------------------------------
    host.classList.add("wb");
    const platform = IS_MAC ? "wb-mac" : /Win/.test(navigator.platform) ? "wb-windows" : "wb-linux";
    document.documentElement.classList.add(platform);
    const sidebar = div("wb-sidebar");
    // The views' tabs are drawn as the output panel's.
    const sidebarHeader = div("wb-panel-header");
    const viewTabs = div("wb-panel-tabs");
    viewTabs.setAttribute("role", "tablist");
    const sidebarBody = div("wb-sidebar-body");
    sidebarHeader.append(viewTabs);
    sidebar.append(sidebarHeader, sidebarBody);
    const sidebarSash = div("wb-sash wb-sash-vertical");
    const main = div("wb-main");
    main.append(editors.element);
    const quickHost = div("wb-quick-host");
    quickHost.append(quick.element);
    host.append(sidebar, sidebarSash, main, quickHost);

    // On a narrow screen the side bar is a drawer over the editor, and showing
    // or hiding the side bar opens or closes the drawer.
    const narrow = window.matchMedia("(max-width: 760px)");
    const sidebarVisible = () => (narrow.matches ? host.classList.contains("wb-drawer-open") : !sidebar.hidden);
    const setSidebar = (visible: boolean) => {
        if (narrow.matches) host.classList.toggle("wb-drawer-open", visible);
        sidebar.hidden = !visible && !narrow.matches;
        sidebarSash.hidden = sidebar.hidden;
        options.sidebarChanged?.(visible);
    };
    narrow.addEventListener("change", () => options.sidebarChanged?.(sidebarVisible()));
    const scrim = div("wb-scrim");
    scrim.addEventListener("click", () => setSidebar(false));
    host.append(scrim);
    const opened = (preview: boolean) => {
        if (narrow.matches && !preview) setSidebar(false);
    };
    const setSidebarWidth = (width: number) => {
        // The drawer of a narrow screen has a width of its own, and the host is too narrow to bound this one.
        const most = narrow.matches ? width : host.clientWidth - 200;
        sidebar.style.width = `${Math.max(170, Math.min(most, width))}px`;
    };
    dragSash(
        sidebarSash,
        (delta, start) => setSidebarWidth(start + delta),
        () => sidebar.getBoundingClientRect().width,
        () => options.sidebarResized?.(parseFloat(sidebar.style.width)),
    );

    // ---- side bar views ----------------------------------------------------
    const explorer = new Explorer(runtime, {
        open: (path, preview) => {
            // A tap opens for good on a touch screen; there is no double tap to pin.
            if (narrow.matches) preview = false;
            void editors.open(path, { preview, focus: !preview && !narrow.matches });
            opened(preview);
        },
        contextMenu: (node, event) => explorerMenu(node, event),
    });
    const search = new SearchView(index, {
        text: (path) => editors.text(path),
        open: (path, line, column, preview) => {
            if (narrow.matches) preview = false;
            void editors.open(path, { line, column, preview, focus: !preview });
            opened(preview);
        },
        display,
        readOnly: (path) => editors.isReadOnly(path),
        replace: (path, edits) => editors.replace(path, edits),
        confirm: async (text, action) => (await options.ask("warn", text, [action])) === action,
        failed: (text, e) =>
            options.reporter.message(
                e === undefined ? "warn" : "error",
                e === undefined ? text : `${text}: ${briefly(e)}`,
            ),
    });
    // The results follow edits in open files and changes on disk.
    editors.onEdit((path) => search.refresh([path]));
    const views = {
        explorer: { title: "Explorer", element: explorer.element },
        search: { title: "Search", element: search.element },
    };
    type ViewId = keyof typeof views;
    const tabs = new Map<ViewId, HTMLButtonElement>();
    for (const [id, view] of Object.entries(views) as [ViewId, (typeof views)[ViewId]][]) {
        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "wb-panel-tab";
        tab.setAttribute("role", "tab");
        tab.textContent = view.title;
        tab.addEventListener("click", () => showView(id));
        tabs.set(id, tab);
        viewTabs.append(tab);
        sidebarBody.append(view.element);
    }
    const selectView = (id: ViewId) => {
        for (const [other, view] of Object.entries(views)) view.element.hidden = other !== id;
        for (const [other, tab] of tabs) {
            tab.classList.toggle("active", other === id);
            tab.setAttribute("aria-selected", String(other === id));
        }
    };
    /** Show a view, and the side bar if it is hidden. */
    const showView = (id: ViewId) => {
        selectView(id);
        if (!sidebarVisible()) setSidebar(true);
    };
    selectView("explorer");
    options.sidebarChanged?.(sidebarVisible());

    // ---- explorer sections ---------------------------------------------------
    const sections = (): Section[] => {
        const list: Section[] = [];
        if (project.tree !== "") {
            list.push({
                id: "project",
                title: project.name,
                root: () => projectNodes(),
                actions: [
                    { icon: "new-file", title: "New File…", run: () => void newEntry(project.tree, "file") },
                    { icon: "new-folder", title: "New Folder…", run: () => void newEntry(project.tree, "dir") },
                    { icon: "refresh", title: "Refresh Explorer", run: () => explorer.refresh() },
                    { icon: "ellipsis", title: "More Actions…", run: () => void quick.show(">View: ") },
                ],
                dropInto: (folder) => {
                    const dir = folder === undefined ? project.tree : folder.kind === "dir" ? folder.path : undefined;
                    return dir === undefined ? undefined : (items) => void dropInto(dir, items);
                },
            });
        }
        if (settings.showIdf.get()) {
            list.push({
                id: "idf",
                title: "ESP-IDF",
                collapsed: true,
                root: async () =>
                    (await runtime.exists(IDF))
                        ? directoryNodes(runtime, IDF)
                        : [
                              {
                                  id: "idf-missing",
                                  label: "Arrives with the toolchain, at the first build",
                                  kind: "file",
                                  dim: true,
                              },
                          ],
            });
        }
        return [...list, ...pageSections];
    };
    let pageSections: Section[] = [];

    const projectNodes = async (): Promise<TreeNode[]> => {
        const build = buildTop();
        const managed = `${project.root}/managed_components`;
        const grouped = settings.groupGenerated.get();
        const base = plainNode(runtime);
        const present = (entry: FsDirEntry, path: string): TreeNode | null => {
            if (grouped && (path === build || path === managed)) return null;
            const node = base(entry, path);
            if (node.kind === "dir") node.children = () => directoryNodes(runtime, path, present);
            if (path === project.root && path !== project.tree) {
                node.badge = "P";
                node.title = `${path}\nidfweb builds this project`;
            }
            if (path === build || path === managed) node.dim = true;
            return node;
        };
        const nodes = await directoryNodes(runtime, project.tree, present);
        if (grouped && (await runtime.exists(managed))) nodes.push(managedGroup(managed));
        if (grouped && (await runtime.exists(build))) nodes.push(buildGroup(project.buildDir, build));
        return nodes;
    };

    /** The registry's components, labeled as the manifest lists them, with their versions. */
    const managedGroup = (managed: string): TreeNode => ({
        id: "group:managed",
        label: "Managed components",
        kind: "group",
        dim: true,
        title: `${managed}\nDownloaded by the component manager`,
        children: async () => {
            const entries = await runtime.fs.readdir(managed);
            const dirs = entries.filter((entry) => entry.filetype === Filetype.DIRECTORY);
            return await Promise.all(
                dirs.map(async (entry): Promise<TreeNode> => {
                    const path = `${managed}/${entry.name}`;
                    const manifest = await editors.text(`${path}/idf_component.yml`);
                    const version = /^version:\s*["']?([^"'\s]+)/m.exec(manifest ?? "")?.[1];
                    return {
                        id: `dir:${path}`,
                        label: entry.name.replace("__", "/"),
                        description: version,
                        kind: "dir",
                        path,
                        children: () => directoryNodes(runtime, path),
                    };
                }),
            );
        },
    });

    /** What the last build produced, first the files people look for, then the whole of `all`. */
    const buildGroup = (build: string, all: string): TreeNode => ({
        id: "group:build",
        label: "Build output",
        kind: "group",
        dim: true,
        title: build,
        children: async () => {
            const top = await runtime.fs.readdir(build).catch(() => []);
            const artifacts = top
                .filter((entry) => entry.filetype !== Filetype.DIRECTORY && /\.(elf|bin|map)$/.test(entry.name))
                .map((entry) => `${build}/${entry.name}`);
            const known = [
                "bootloader/bootloader.bin",
                "partition_table/partition-table.bin",
                "compile_commands.json",
                "config/sdkconfig.h",
            ].map((path) => `${build}/${path}`);
            for (const path of known) if (await runtime.exists(path)) artifacts.push(path);
            const files = artifacts.map((path): TreeNode => ({
                id: `path:${path}`,
                label: path.slice(build.length + 1),
                kind: "file",
                path,
            }));
            return [
                ...files,
                {
                    id: "all:build",
                    label: "All files",
                    kind: "dir",
                    path: all,
                    children: () => directoryNodes(runtime, all),
                },
            ];
        },
    });

    const refreshExplorer = () => {
        explorer.setSections(sections());
        index.configure([project.tree, ...(settings.showIdf.get() ? [IDF] : [])].filter(Boolean), [buildTop()]);
    };
    settings.showIdf.onChange(refreshExplorer);
    settings.groupGenerated.onChange(refreshExplorer);

    const newEntry = async (dir: string, kind: "file" | "dir") => {
        const name = await quick.ask(kind === "file" ? "New file name" : "New folder name");
        if (!name) return;
        const path = `${dir}/${name}`;
        try {
            const parts = name.split("/");
            let at = dir;
            for (const part of parts.slice(0, kind === "file" ? -1 : undefined)) {
                at = `${at}/${part}`;
                if (!(await runtime.exists(at))) await runtime.fs.mkdir(at);
            }
            if (kind === "file") {
                await runtime.fs.writeFile(path, new Uint8Array(), { create: true, overwrite: false });
                await editors.open(path);
            }
        } catch (e) {
            options.reporter.message("error", `Could not create ${name}: ${briefly(e)}`);
        }
        explorer.refresh();
        index.invalidate();
    };

    const dropInto = async (dir: string, items: Promise<Dropped[]>) => {
        let paths;
        try {
            paths = await copyDropped(
                { fs: runtime.fs, ask: options.ask, reporter: options.reporter },
                dir,
                await items,
            );
        } catch (e) {
            options.reporter.message("error", `Could not read the dropped files: ${briefly(e)}`);
        }
        explorer.refresh();
        index.invalidate();
        if (paths?.[0] !== undefined) await explorer.reveal(paths[0]);
    };

    const explorerMenu = (node: TreeNode, event: MouseEvent) => {
        const path = node.path;
        if (path === undefined) return;
        const dir = node.kind === "file" ? path.slice(0, path.lastIndexOf("/")) : path;
        const editable = !path.startsWith(IDF + "/") && path !== IDF;
        showMenu(event, [
            ...(editable
                ? [
                      { label: "New File…", run: () => void newEntry(dir, "file") },
                      { label: "New Folder…", run: () => void newEntry(dir, "dir") },
                      null,
                  ]
                : []),
            { label: "Copy Path", run: () => void navigator.clipboard.writeText(path) },
            { label: "Copy Relative Path", run: () => void navigator.clipboard.writeText(display(path).relative) },
            ...(editable
                ? [
                      null,
                      { label: "Rename…", run: () => void rename(path) },
                      { label: "Delete", run: () => void remove(path, node.kind !== "file") },
                  ]
                : []),
            ...(node.kind === "dir" && editable
                ? [null, { label: "Use as Project Root", run: () => void selectProject(path) }]
                : []),
        ]);
    };

    const rename = async (path: string) => {
        const name = path.slice(path.lastIndexOf("/") + 1);
        const dot = name.lastIndexOf(".");
        const answer = await quick.ask("New name", name, [0, dot > 0 ? dot : name.length]);
        if (!answer || answer === name) return;
        const to = `${path.slice(0, path.lastIndexOf("/"))}/${answer}`;
        try {
            await editors.saveAll();
            await runtime.fs.rename(path, to);
            editors.closeTree(path, { save: false });
        } catch (e) {
            options.reporter.message("error", `Could not rename ${name}: ${briefly(e)}`);
        }
        explorer.refresh();
        index.invalidate();
    };

    const remove = async (path: string, recursive: boolean) => {
        const name = path.slice(path.lastIndexOf("/") + 1);
        if (!window.confirm(`Delete ${name}${recursive ? " and everything in it" : ""}?`)) return;
        try {
            editors.closeTree(path, { save: false });
            await runtime.fs.delete(path, recursive);
        } catch (e) {
            options.reporter.message("error", `Could not delete ${name}: ${briefly(e)}`);
        }
        explorer.refresh();
        index.invalidate();
    };

    /** A link in rendered Markdown: a folder is shown in the explorer, a file opened, `#L12` is its line 12. */
    const followLink = async (path: string, fragment: string) => {
        const stat = await runtime.fs.stat(path).catch(() => undefined);
        if (stat === undefined) {
            options.reporter.message("warn", `The link leads to ${path}, which is not here.`);
        } else if (stat.filetype === Filetype.DIRECTORY) {
            showView("explorer");
            void explorer.reveal(path, true);
        } else {
            const line = /^L(\d+)$/.exec(fragment)?.[1];
            const anchor = line === undefined && fragment !== "" ? fragment : undefined;
            void editors.open(path, {
                line: line === undefined ? undefined : Number(line),
                rendered: isMarkdown(path),
                anchor,
            });
        }
    };

    // ---- project root --------------------------------------------------------
    const waiting = new Map<number, (reply: ProjectReply) => void>();
    let nextId = 1;
    const ask = (message: ProjectRequest) =>
        new Promise<ProjectReply>((resolve) => {
            waiting.set(message.id, resolve);
            runtime.post(message);
        });
    const selectProject = async (path: string) => {
        const reply = await ask({ type: "select-project", id: nextId++, path });
        if (reply.type === "select-project" && reply.error !== undefined) {
            options.reporter.message("error", reply.error);
            return;
        }
        showView("explorer");
        void explorer.reveal(path, true);
    };
    const pickProject = async () => {
        const reply = await ask({ type: "projects", id: nextId++ });
        if (reply.type !== "projects") return;
        if (reply.projects.length === 0) {
            options.reporter.message(
                "warn",
                "No ESP-IDF project here. A project's CMakeLists.txt includes tools/cmake/project.cmake.",
            );
            return;
        }
        quick.show("", {
            prefix: "",
            placeholder: "Select the project to build",
            items: (query) =>
                reply.projects
                    .map((path) => ({ path, label: display(path).relative || project.name }))
                    .map((p) => ({ ...p, match: fuzzy(query, p.label) }))
                    .filter((p) => p.match !== undefined)
                    .map((p) => ({
                        label: p.label,
                        highlights: p.match!.positions,
                        description: p.path === reply.root ? "selected" : undefined,
                        accept: () => void selectProject(p.path),
                    })),
        });
    };

    // ---- configure ------------------------------------------------------------
    const configuring = new Map<number, (error: string | undefined) => void>();
    const configure = (force: boolean) =>
        new Promise<string | undefined>((resolve) => {
            const id = nextId++;
            configuring.set(id, resolve);
            runtime.post({ type: "configure", id, force });
        });
    /** Open the project root's sdkconfig in the editor, configuring first when no build has, as `idf.py menuconfig` does. */
    const openConfiguration = async () => {
        const error = await configure(false);
        if (error !== undefined) options.reporter.message("error", `The project could not be configured: ${error}.`);
        else await editors.open(`${project.root}/sdkconfig`, { preview: false, rendered: true });
    };

    // ---- builds ---------------------------------------------------------------
    let settle: ((ok: boolean) => void) | undefined;
    /**
     * Build, and show the output when it ends. A Run's build that succeeded
     * leaves the panel alone, since the flash shows the monitor next.
     */
    const build = async (reconfigure: boolean, forRun = false): Promise<boolean> => {
        if (settle !== undefined) {
            options.reporter.message("warn", "A build is already running.");
            return false;
        }
        const done = new Promise<boolean>((resolve) => {
            settle = resolve;
        });
        try {
            await editors.saveAll();
        } catch (e) {
            options.reporter.message("error", `Could not save before the build: ${briefly(e)}`);
            finish(false);
            return await done;
        }
        panel.append(OUTPUT, `\n--- ${reconfigure ? "configure and build" : "build"} ---\n`);
        runtime.post({ type: "build", jobs: Math.max(1, (navigator.hardwareConcurrency || 4) - 1), reconfigure });
        const ok = await done;
        if (!ok || !forRun) panel.show(OUTPUT);
        return ok;
    };
    const finish = (ok: boolean) => {
        const waiter = settle;
        settle = undefined;
        waiter?.(ok);
    };
    const run = async () => {
        if (!(await build(false, true))) return;
        // The device pane reports the flash, and why it failed, as a task.
        try {
            await options.flash();
        } catch (e) {
            panel.append(OUTPUT, `the flash failed: ${briefly(e)}\n`);
        }
    };

    // clangd reads compile_commands.json when it starts, and the first build
    // writes it; a configure rewrites it. Either needs a new clangd.
    let compileCommands = 0;
    const restartOnNewCompileCommands = async () => {
        const mtime = await runtime.fs.stat(`${project.buildDir}/compile_commands.json`).then(
            (stat) => stat.mtimeMs,
            () => 0,
        );
        if (mtime === compileCommands) return;
        compileCommands = mtime;
        clangd.restart();
    };

    // ---- open editors ------------------------------------------------------------
    // Tabs closed and opened with a project are reported once, when all are open.
    let restoring = false;
    /** The view tabs a saved list can open: the workbench's own. */
    let ownViews = (): ViewTab[] => [];
    let reported = "";
    const reportEditors = () => {
        if (restoring) return;
        const state = editors.openEditors;
        const json = JSON.stringify(state);
        if (json === reported) return;
        reported = json;
        options.editorsChanged?.(state);
    };
    /**
     * Open the tabs `saved` lists, in order and in their groups; a file that is
     * gone, or a view the workbench does not have, stays closed, and so does a
     * group left without tabs.
     */
    const restoreEditors = async (saved: OpenEditors) => {
        const open = async (key: string) => {
            if (key.startsWith("/")) {
                const rendered = saved.rendered.includes(key);
                await editors.open(key, { focus: false, preview: key === saved.preview, rendered });
                return;
            }
            const view = ownViews().find((spec) => viewKey(spec.id) === key);
            if (view !== undefined) editors.openView(view, { focus: false });
        };
        const groups = editors.arrange(saved.groups);
        if (groups.length === 0) groups.push({ tabs: saved.tabs });
        for (const [i, group] of groups.entries()) {
            editors.focusGroup(i);
            for (const key of group.tabs) await open(key);
            if (group.active !== undefined) await open(group.active);
        }
        editors.closeEmptyGroups();
        if (saved.active !== undefined) await open(saved.active);
    };

    // ---- runtime events ---------------------------------------------------------
    runtime.watch(GUEST_PROJECT, true);
    runtime.on((message: RuntimeToWorkbench) => {
        switch (message.type) {
            case "ready":
                panel.append(OUTPUT, "the toolchain is ready\n");
                if (settings.showIdf.get()) explorer.refresh();
                return;
            case "opened": {
                project.tree = message.tree;
                project.name = message.tree.slice(message.tree.lastIndexOf("/") + 1);
                const saved = options.savedEditors?.();
                restoring = true;
                // The old project's files are gone; saving its edits would write them into the new one.
                editors.closeAll({ save: false });
                refreshExplorer();
                const readme = `${message.tree}/README.md`;
                const reopened =
                    saved !== undefined
                        ? restoreEditors(saved)
                        : editors.open(readme, { focus: false, rendered: true }).then((view) => {
                              if (view !== null) void explorer.reveal(readme);
                          });
                void reopened.finally(() => {
                    restoring = false;
                    history.clear();
                    reportEditors();
                });
                return;
            }
            case "project-root":
                project.root = message.root;
                project.buildDir = message.buildDir;
                refreshExplorer();
                clangd.restart();
                if (message.choose !== undefined) {
                    const count = message.choose.length;
                    void (options.chooseProject?.(message.root, message.choose) ?? Promise.resolve(false)).then(
                        (chosen) => {
                            if (chosen) return;
                            options.reporter.message(
                                "info",
                                `This folder holds ${count} ESP-IDF projects and none at its top. Choose one.`,
                            );
                            void pickProject();
                        },
                    );
                }
                return;
            case "projects":
            case "select-project":
                waiting.get(message.id)?.(message);
                waiting.delete(message.id);
                return;
            case "output":
                panel.append(OUTPUT, message.text);
                return;
            case "changed":
                // The top of the tree and the project root are drawn from fresh listings.
                if (message.directories.some((d) => d === project.tree || d === project.root)) {
                    explorer.refresh();
                } else explorer.changed(message.directories);
                if (message.directories.length > 0) index.invalidate();
                void editors.reload(message.files);
                search.refresh(message.files, message.directories.length > 0);
                return;
            case "built":
                if (settle !== undefined)
                    options.event?.({ kind: "built", ok: message.ok, elf: message.elf, message: message.message });
                finish(message.ok);
                void restartOnNewCompileCommands();
                // The build output group's rows are not directories the worker reports as changed.
                if (settings.groupGenerated.get()) explorer.refresh();
                // The worker reports how the build ended as a task; the panel keeps the details.
                if (message.ok) panel.append(OUTPUT, `\nbuilt ${message.elf ?? ""}\n`);
                else panel.append(OUTPUT, `\nthe build failed: ${message.message ?? "see above"}\n`);
                return;
            case "configure":
                configuring.get(message.id)?.(message.error);
                configuring.delete(message.id);
                return;
            case "failed":
                // Also a toolchain download that failed outside a build, which ends no build.
                if (settle !== undefined) options.event?.({ kind: "built", ok: false, message: message.message });
                finish(false);
                panel.append(OUTPUT, message.message + "\n");
                panel.show(OUTPUT);
                return;
        }
    });

    // ---- quick input providers ---------------------------------------------------
    quick.register({
        prefix: "",
        placeholder: "Search files by name (append : to go to a line, or type > for commands)",
        items: async (query) => {
            const recent = editors.recent;
            if (query === "") {
                return recent.map((path, i) => fileItem(path, [], i === 0 ? "recently opened" : undefined));
            }
            const [name, line] = query.split(":");
            const files = await index.files();
            const scored = files
                .map((path) => {
                    const { relative } = display(path);
                    const base = relative.slice(relative.lastIndexOf("/") + 1);
                    const onName = fuzzy(name!, base);
                    const onPath = onName ?? fuzzy(name!, relative);
                    if (onPath === undefined) return undefined;
                    const bonus = (onName ? 100 : 0) + (recent.includes(path) ? 50 : 0);
                    return { path, score: onPath.score + bonus, positions: onName ? onName.positions : [] };
                })
                .filter((x) => x !== undefined)
                .sort((a, b) => b.score - a.score)
                .slice(0, 100);
            return scored.map((s) => fileItem(s.path, s.positions, undefined, line ? Number(line) : undefined));
        },
    });
    const fileItem = (path: string, highlights: number[], separator?: string, line?: number): QuickItem => {
        const { name, folder } = display(path);
        return {
            label: name,
            description: path.startsWith(IDF + "/") ? `esp-idf/${folder}` : folder,
            icon: fileIcon(path),
            highlights,
            separator,
            accept: () => void editors.open(path, { line }),
        };
    };
    quick.register({
        prefix: ">",
        placeholder: "Type the name of a command to run",
        items: (query) =>
            registry
                .all()
                .map((command) => {
                    const label = command.category ? `${command.category}: ${command.title}` : command.title;
                    return { command, label, match: fuzzy(query, label) };
                })
                .filter((c) => c.match !== undefined)
                .sort((a, b) => (query ? b.match!.score - a.match!.score : a.label.localeCompare(b.label)))
                .map(({ command, label, match }) => ({
                    label,
                    highlights: match!.positions,
                    keybinding: command.keys?.[0] === undefined ? undefined : keyLabel(command.keys[0]),
                    accept: () => void registry.run(command),
                })),
    });
    quick.register({
        prefix: ":",
        placeholder: "Type a line number, then a colon and a column, to go to",
        items: () => [],
        acceptEmpty: (query) => {
            const view = editors.activeView;
            const [line, column] = query.split(":").map(Number);
            if (view === undefined || !line) return false;
            void editors.open(editors.activePath!, { line, column: column || 1 });
            return true;
        },
    });
    quick.register({
        prefix: "@",
        placeholder: "Go to a symbol in the editor",
        items: async (query) => {
            const path = editors.activePath;
            if (path === undefined) return [];
            const symbols = await clangd
                .request<DocumentSymbol[]>("textDocument/documentSymbol", { textDocument: { uri: `file://${path}` } })
                .catch(() => null);
            return flattenSymbols(symbols ?? [])
                .map((s) => ({ s, match: fuzzy(query, s.name) }))
                .filter((x) => x.match !== undefined)
                .sort((a, b) => (query ? b.match!.score - a.match!.score : 0))
                .map(({ s, match }) => ({
                    label: s.name,
                    description: s.detail,
                    highlights: match!.positions,
                    icon: symbolIcon(s.kind),
                    accept: () => void editors.open(path, { line: s.line + 1, column: s.character + 1 }),
                }));
        },
    });
    quick.register({
        prefix: "#",
        placeholder: "Go to a symbol in the project",
        hint: "Start typing a function, variable or type name",
        items: async (query) => {
            if (query === "") return [];
            const symbols = await clangd.request<WorkspaceSymbol[]>("workspace/symbol", { query }).catch(() => null);
            return (symbols ?? []).map((s) => {
                const path = pathOf(s.location.uri);
                return {
                    label: s.name,
                    description: `${s.containerName ? s.containerName + " · " : ""}${display(path).relative}`,
                    icon: symbolIcon(s.kind),
                    highlights: fuzzy(query, s.name)?.positions,
                    accept: () =>
                        void editors.open(path, {
                            line: s.location.range.start.line + 1,
                            column: s.location.range.start.character + 1,
                        }),
                };
            });
        },
    });

    // ---- commands -------------------------------------------------------------------
    const inEditor = (run: (view: EditorView) => boolean) => () => {
        const view = editors.activeView;
        if (view !== undefined) {
            view.focus();
            run(view);
        }
    };
    const hasEditor = () => editors.activeView !== undefined;
    const shortcuts = shortcutsTab(registry);
    const settingsView = settingsTab(settingsRegistry);
    ownViews = () => [shortcuts, settingsView];
    registry.register(
        { id: "workbench.action.quickOpen", title: "Go to File…", keys: ["Mod-p", "Mod-e"], run: () => quick.show("") },
        {
            id: "workbench.action.showCommands",
            title: "Show All Commands",
            keys: ["Mod-Shift-p", "F1"],
            run: () => quick.show(">"),
        },
        {
            id: "workbench.action.gotoLine",
            title: "Go to Line/Column…",
            category: "Go",
            keys: ["Ctrl-g"],
            run: () => quick.show(":"),
            enabled: hasEditor,
        },
        {
            id: "workbench.action.gotoSymbol",
            title: "Go to Symbol in Editor…",
            category: "Go",
            keys: ["Mod-Shift-o"],
            run: () => quick.show("@"),
            enabled: hasEditor,
        },
        {
            id: "workbench.action.showAllSymbols",
            title: "Go to Symbol in Workspace…",
            category: "Go",
            keys: ["Mod-t"],
            run: () => quick.show("#"),
        },
        {
            id: "workbench.view.explorer",
            title: "Show Explorer",
            category: "View",
            keys: ["Mod-Shift-e"],
            run: () => {
                showView("explorer");
                explorer.element.focus();
            },
        },
        {
            id: "workbench.view.search",
            title: "Search: Find in Files",
            keys: ["Mod-Shift-f"],
            run: () => {
                showView("search");
                const view = editors.activeView;
                const selection = view?.state.sliceDoc(view.state.selection.main.from, view.state.selection.main.to);
                search.focus(selection && !selection.includes("\n") ? selection : undefined);
            },
        },
        {
            id: "workbench.action.replaceInFiles",
            title: "Search: Replace in Files",
            keys: ["Mod-Shift-h"],
            run: () => {
                showView("search");
                search.toggleReplace(true);
                const view = editors.activeView;
                const selection = view?.state.sliceDoc(view.state.selection.main.from, view.state.selection.main.to);
                search.focus(selection && !selection.includes("\n") ? selection : undefined);
            },
        },
        {
            id: "workbench.action.toggleSidebarVisibility",
            title: "Toggle Left Side Bar Visibility",
            category: "View",
            keys: ["Mod-b"],
            run: () => setSidebar(!sidebarVisible()),
        },
        {
            id: "workbench.action.output.toggleOutput",
            title: "Show Build Output",
            category: "View",
            keys: ["Mod-Shift-u"],
            run: () => panel.show(OUTPUT),
        },
        {
            id: "workbench.action.files.save",
            title: "Save",
            category: "File",
            keys: ["Mod-s"],
            run: () => editors.save(),
            enabled: hasEditor,
        },
        {
            id: "workbench.action.files.saveAll",
            title: "Save All",
            category: "File",
            keys: ["Mod-Alt-s"],
            run: () => editors.saveAll(),
        },
        {
            id: "workbench.action.closeActiveEditor",
            title: "Close Editor",
            category: "View",
            keys: ["Mod-w", "Ctrl-F4"],
            run: () => editors.close(),
            enabled: () => editors.hasActive,
        },
        {
            id: "workbench.action.closeAllEditors",
            title: "Close All Editors",
            category: "View",
            keys: [],
            run: () => editors.closeAll(),
        },
        {
            id: "workbench.action.nextEditor",
            title: "Open Next Editor",
            category: "View",
            keys: ["Alt-PageDown", "Ctrl-Tab", "Ctrl-PageDown", "Mod-Alt-ArrowRight"],
            run: () => editors.cycle(1),
        },
        {
            id: "workbench.action.previousEditor",
            title: "Open Previous Editor",
            category: "View",
            keys: ["Alt-PageUp", "Ctrl-Shift-Tab", "Ctrl-PageUp", "Mod-Alt-ArrowLeft"],
            run: () => editors.cycle(-1),
        },
        {
            id: "workbench.action.splitEditor",
            title: "Split Editor Right",
            category: "View",
            keys: ["Mod-\\"],
            run: () => editors.split("right"),
            enabled: () => editors.canSplit,
        },
        {
            id: "workbench.action.splitEditorDown",
            title: "Split Editor Down",
            category: "View",
            keys: [],
            run: () => editors.split("down"),
            enabled: () => editors.canSplit,
        },
        {
            id: "workbench.action.moveEditorToNextGroup",
            title: "Move Editor into Next Group",
            category: "View",
            keys: [],
            run: () => editors.moveToGroup(1),
            enabled: () => (editors.groupCount > 1 ? editors.hasActive : editors.canSplit),
        },
        {
            id: "workbench.action.moveEditorToPreviousGroup",
            title: "Move Editor into Previous Group",
            category: "View",
            keys: [],
            run: () => editors.moveToGroup(-1),
            enabled: () => (editors.groupCount > 1 ? editors.hasActive : editors.canSplit),
        },
        {
            id: "workbench.action.focusNextGroup",
            title: "Focus Next Editor Group",
            category: "View",
            keys: [],
            run: () => editors.cycleGroup(1),
            enabled: () => editors.groupCount > 1,
        },
        {
            id: "workbench.action.focusPreviousGroup",
            title: "Focus Previous Editor Group",
            category: "View",
            keys: [],
            run: () => editors.cycleGroup(-1),
            enabled: () => editors.groupCount > 1,
        },
        {
            id: "workbench.action.navigateBack",
            title: "Go Back",
            keys: [IS_MAC ? "Ctrl--" : "Ctrl-Alt--"],
            run: () => history.back(),
        },
        {
            id: "workbench.action.navigateForward",
            title: "Go Forward",
            keys: ["Ctrl-Shift--"],
            run: () => history.forward(),
        },
        {
            id: "editor.action.startFindReplaceAction",
            title: "Replace",
            category: "Editor",
            keys: ["Mod-h", "Mod-Alt-f"],
            run: inEditor(openReplace),
            enabled: hasEditor,
        },
        {
            id: "editor.action.revealDefinition",
            title: "Go to Definition",
            category: "Editor",
            run: inEditor(jumpToDefinition),
            enabled: hasEditor,
        },
        {
            id: "editor.action.revealDeclaration",
            title: "Go to Declaration",
            category: "Editor",
            run: inEditor(jumpToDeclaration),
            enabled: hasEditor,
        },
        {
            id: "editor.action.goToReferences",
            title: "Find All References",
            category: "Editor",
            run: inEditor(findReferences),
            enabled: hasEditor,
        },
        {
            id: "editor.action.rename",
            title: "Rename Symbol",
            category: "Editor",
            run: inEditor(renameSymbol),
            enabled: hasEditor,
        },
        {
            id: "editor.action.toggleWordWrap",
            get title() {
                return editors.wordWrap ? "Disable Word Wrap" : "Enable Word Wrap";
            },
            category: "Editor",
            keys: ["Alt-z"],
            run: () => {
                settings.wordWrap.set(!editors.wordWrap);
            },
        },
        {
            id: "markdown.showPreview",
            title: "Open Preview",
            category: "Markdown",
            keys: ["Mod-Shift-v"],
            run: () => editors.setRendered(true),
            enabled: () => isMarkdown(editors.activePath ?? "") && !editors.activeRendered,
        },
        {
            id: "markdown.showSource",
            title: "Open Source",
            category: "Markdown",
            keys: ["Mod-Shift-v"],
            run: () => editors.setRendered(false),
            enabled: () => editors.activeRendered,
        },
        {
            id: "idfweb.sdkconfig.showForm",
            title: "Open Interactive Editor",
            category: "sdkconfig",
            run: () => editors.setRendered(true),
            enabled: () => isSdkconfig(editors.activePath) && !editors.activeRendered,
        },
        {
            id: "idfweb.sdkconfig.showText",
            title: "Open as Text",
            category: "sdkconfig",
            run: () => editors.setRendered(false),
            enabled: () => isSdkconfig(editors.activePath) && editors.activeRendered,
        },
        {
            id: "idfweb.manifest.showForm",
            title: "Open Manifest Editor",
            category: "Component Manifest",
            run: () => editors.setRendered(true),
            enabled: () => isManifest(editors.activePath) && !editors.activeRendered,
        },
        {
            id: "idfweb.manifest.showText",
            title: "Open as Text",
            category: "Component Manifest",
            run: () => editors.setRendered(false),
            enabled: () => isManifest(editors.activePath) && editors.activeRendered,
        },
        {
            id: "editor.action.formatDocument",
            title: "Format Document",
            category: "Editor",
            run: inEditor(formatDocument),
            enabled: hasEditor,
        },
        { id: "clangd.restart", title: "Restart language server", category: "clangd", run: () => clangd.restart() },
        { id: "idfweb.build", title: "Build", category: "idfweb", keys: ["Mod-Shift-b"], run: () => build(false) },
        { id: "idfweb.rebuild", title: "Configure and Build", category: "idfweb", run: () => build(true) },
        { id: "idfweb.configure", title: "Configure", category: "idfweb", run: () => void configure(true) },
        {
            id: "idfweb.openConfiguration",
            title: "Open Project Configuration",
            category: "idfweb",
            run: () => void openConfiguration(),
        },
        { id: "idfweb.run", title: "Build and Flash", category: "idfweb", run: () => run() },
        { id: "idfweb.selectProject", title: "Select Project Root…", category: "idfweb", run: () => pickProject() },
        {
            id: "idfweb.explorer.toggleIdf",
            get title() {
                return settings.showIdf.get() ? "Hide ESP-IDF in Explorer" : "Show ESP-IDF in Explorer";
            },
            category: "View",
            run: () => {
                settings.showIdf.set(!settings.showIdf.get());
            },
        },
        {
            id: "idfweb.explorer.toggleGrouping",
            get title() {
                return settings.groupGenerated.get()
                    ? "Show Build and Managed Components in Place"
                    : "Group Build and Managed Components";
            },
            category: "View",
            run: () => {
                settings.groupGenerated.set(!settings.groupGenerated.get());
            },
        },
        {
            id: "workbench.action.openGlobalKeybindings",
            title: "Open Keyboard Shortcuts",
            category: "Preferences",
            run: () => editors.openView(shortcuts),
        },
        {
            id: "workbench.action.openSettings",
            title: "Open Settings",
            category: "Preferences",
            keys: ["Mod-,"],
            run: () => editors.openView(settingsView),
        },
    );
    registry.describe(...editorKeys(), ...findKeys());
    registry.onChange(() => editors.keysChanged());
    editors.keysChanged();
    registry.listen(window);

    // Unsaved edits are saved after a second; a tab closed sooner asks.
    window.addEventListener("beforeunload", (event) => {
        if (editors.dirty.length > 0) event.preventDefault();
    });
    editors.onChange(reportEditors);
    // The explorer follows the active editor, as VS Code's `explorer.autoReveal` does.
    let revealed: string | undefined;
    editors.onChange(() => {
        const path = editors.activePath;
        if (path === revealed) return;
        revealed = path;
        if (path !== undefined) void explorer.reveal(path);
    });

    return {
        execute: (id) => registry.execute(id),
        command: (id) => registry.get(id),
        commands: () => registry.all(),
        register: (...commands) => registry.register(...commands),
        quickPrefix: () => quick.prefix,
        hideQuick: () => quick.hide(),
        panel,
        setTargets: (targets) => (idfTargets = targets),
        openView: (tab) => editors.openView(tab),
        openEditors: () => editors.openEditors,
        showLines: (path, pick, note) => editors.showLines(path, pick, note),
        showSetting: async (path, name) => {
            if ((await editors.open(path, { preview: false, rendered: true })) === null)
                return `${path} does not exist.`;
            return revealSetting(path, name);
        },
        open: async (path) =>
            (await editors.open(path, { preview: false, ...(isMarkdown(path) ? { rendered: true } : {}) })) !== null,
        navigation: () => ({ back: history.canGoBack, forward: history.canGoForward }),
        onNavigation: (listener) => history.onChange(listener),
        sidebarVisible,
        setSidebar: (visible) => {
            if (visible !== sidebarVisible()) setSidebar(visible);
        },
        setSidebarWidth,
        setPageSections: (sections) => {
            pageSections = sections;
            refreshExplorer();
        },
        refreshExplorer: () => explorer.refresh(),
    };
}

interface DocumentSymbol {
    name: string;
    detail?: string;
    kind: number;
    range?: { start: { line: number; character: number } };
    selectionRange?: { start: { line: number; character: number } };
    location?: { range: { start: { line: number; character: number } } };
    children?: DocumentSymbol[];
}

interface WorkspaceSymbol {
    name: string;
    kind: number;
    containerName?: string;
    location: { uri: string; range: { start: { line: number; character: number } } };
}

function flattenSymbols(
    symbols: DocumentSymbol[],
    prefix = "",
): { name: string; detail?: string; kind: number; line: number; character: number }[] {
    return symbols.flatMap((s) => {
        const start = s.selectionRange?.start ?? s.range?.start ?? s.location?.range.start ?? { line: 0, character: 0 };
        const name = prefix ? `${prefix}::${s.name}` : s.name;
        return [
            {
                name: s.name,
                detail: s.detail ?? (prefix || undefined),
                kind: s.kind,
                line: start.line,
                character: start.character,
            },
            ...flattenSymbols(s.children ?? [], name),
        ];
    });
}

/** LSP SymbolKind as the codicon VS Code draws for it. */
function symbolIcon(kind: number): HTMLElement {
    const names: Record<number, string> = {
        5: "class",
        6: "method",
        8: "field",
        9: "method",
        10: "enum",
        11: "interface",
        12: "method",
        13: "variable",
        14: "constant",
        22: "enum-member",
        23: "structure",
        26: "type-parameter",
    };
    const element = document.createElement("span");
    element.className = `codicon codicon-symbol-${names[kind] ?? "misc"} wb-symbol-icon wb-symbol-${kind}`;
    return element;
}

function div(className: string): HTMLDivElement {
    const element = document.createElement("div");
    element.className = className;
    return element;
}

function dragSash(
    sash: HTMLElement,
    move: (delta: number, start: number) => void,
    size: () => number,
    end: () => void,
): void {
    let origin = 0;
    let start = 0;
    sash.addEventListener("pointerdown", (event) => {
        sash.setPointerCapture(event.pointerId);
        origin = event.clientX;
        start = size();
        document.body.classList.add("wb-dragging-x");
        event.preventDefault();
    });
    sash.addEventListener("pointermove", (event) => {
        if (!sash.hasPointerCapture(event.pointerId)) return;
        move(event.clientX - origin, start);
    });
    const stop = (event: PointerEvent) => {
        if (!sash.hasPointerCapture(event.pointerId)) return;
        sash.releasePointerCapture(event.pointerId);
        document.body.classList.remove("wb-dragging-x");
        end();
    };
    sash.addEventListener("pointerup", stop);
    sash.addEventListener("pointercancel", stop);
}

function briefly(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}
